import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, RefreshCw } from "lucide-react";
import { explorerApi } from "./api";
import type { ExplorerEntity, ExplorerTimeRange } from "./types";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import { useToast } from "@/app/providers/Toaster";
import { cn } from "@/lib/utils/cn";

const ENTITY_OPTIONS: { value: ExplorerEntity; label: string }[] = [
  { value: "users", label: "Usuarios" },
  { value: "orders", label: "Órdenes" },
  { value: "events", label: "Eventos" },
  { value: "tickets", label: "Tickets" },
  { value: "staff", label: "Staff" },
  { value: "settlements", label: "Liquidaciones" },
];

const TIME_RANGES: { value: ExplorerTimeRange; label: string }[] = [
  { value: "24h", label: "24h" },
  { value: "168h", label: "7 días" },
  { value: "720h", label: "30 días" },
  { value: "all", label: "Todo" },
];

export function DataExplorerScreen() {
  const [entity, setEntity] = useState<ExplorerEntity>("users");
  const [timeRange, setTimeRange] = useState<ExplorerTimeRange>("24h");
  const [search, setSearch] = useState("");

  const { push } = useToast();

  const query = useQuery({
    queryKey: ["operations", "explorer", { entity, timeRange }],
    queryFn: () => explorerApi.query(entity, { take: 5000, timeRange }),
  });

  const rows = query.data?.data ?? [];

  const columns = useMemo(() => {
    if (rows.length === 0) return [];
    return Object.keys(rows[0]);
  }, [rows]);

  const filtered = useMemo(() => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter((row) =>
      columns.some((c) => String(row[c] ?? "").toLowerCase().includes(q)),
    );
  }, [rows, columns, search]);

  const exportCSV = () => {
    if (filtered.length === 0) {
      push({ kind: "error", message: "No hay datos para exportar." });
      return;
    }
    const header = columns.join(",");
    const csvRow = (row: any) =>
      columns
        .map((c) => {
          const v = row[c];
          if (v === null || v === undefined) return "";
          const s = String(v);
          if (s.includes(",") || s.includes('"') || s.includes("\n")) {
            return `"${s.replace(/"/g, '""')}"`;
          }
          return s;
        })
        .join(",");
    const csv = [header, ...filtered.map(csvRow)].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mixcore-${entity}-${timeRange}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    push({ kind: "success", message: `Descargados ${filtered.length} registros.` });
  };

  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="nx-eyebrow mb-1">Operaciones · Explorador</p>
          <h1 className="nx-h1">Explorador de datos</h1>
          <p className="text-sm text-fg-muted mt-1">
            Consulta rápida cross-entity para debugging y reportes ad-hoc.
            Máx 5000 registros por consulta. Para análisis serio, usá Dashboard
            o exportar CSV.
          </p>
        </div>
        <button
          onClick={exportCSV}
          disabled={filtered.length === 0}
          className="nx-btn-secondary"
        >
          <Download size={14} /> Exportar CSV ({filtered.length.toLocaleString("es-CL")})
        </button>
      </header>

      {/* Tabs entity */}
      <div className="flex flex-wrap gap-1 bg-bg-subtle border border-border rounded-md p-0.5">
        {ENTITY_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setEntity(opt.value)}
            className={cn(
              "px-3 py-1.5 text-xs font-medium rounded-sm transition-colors",
              entity === opt.value
                ? "bg-bg-card text-fg shadow-nx-sm"
                : "text-fg-muted hover:text-fg",
            )}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filtrar en los resultados (client-side)…"
          className="nx-input flex-1 min-w-[200px]"
        />
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value as ExplorerTimeRange)}
          className="nx-input w-auto"
        >
          {TIME_RANGES.map((t) => (
            <option key={t.value} value={t.value}>
              Últimos {t.label}
            </option>
          ))}
        </select>
        <button
          onClick={() => query.refetch()}
          disabled={query.isFetching}
          className="nx-btn-secondary"
        >
          <RefreshCw size={14} className={query.isFetching ? "animate-spin" : ""} />
          Actualizar
        </button>
      </div>

      {query.isLoading ? (
        <LoadingBlock label="Cargando datos…" />
      ) : query.isError ? (
        <ErrorBlock onRetry={() => query.refetch()} />
      ) : (
        <DataTable rows={filtered} columns={columns} total={rows.length} />
      )}
    </div>
  );
}

function DataTable({
  rows,
  columns,
  total,
}: {
  rows: Array<Record<string, any>>;
  columns: string[];
  total: number;
}) {
  if (rows.length === 0) {
    return (
      <div className="nx-card p-8 text-center text-fg-subtle text-sm">
        Sin datos para el filtro/rango actual.
      </div>
    );
  }

  return (
    <div className="nx-card overflow-hidden">
      <div className="overflow-auto max-h-[70vh]">
        <table className="nx-table">
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                {columns.map((c) => (
                  <td key={c}>
                    <CellValue value={row[c]} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 border-t border-border bg-bg-surface text-2xs text-fg-subtle text-right tabular-nums">
        {rows.length === total
          ? `${rows.length.toLocaleString("es-CL")} registros`
          : `${rows.length.toLocaleString("es-CL")} de ${total.toLocaleString("es-CL")} (filtrado)`}
      </div>
    </div>
  );
}

function CellValue({ value }: { value: any }) {
  if (value === null || value === undefined || value === "") {
    return <span className="text-fg-subtle">—</span>;
  }
  if (typeof value === "boolean") {
    return (
      <span className={value ? "nx-badge-success" : "nx-badge-neutral"}>
        {value ? "Sí" : "No"}
      </span>
    );
  }
  // ISO date string heuristic
  if (
    typeof value === "string" &&
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)
  ) {
    try {
      return (
        <span className="text-fg-muted text-xs tabular-nums">
          {new Date(value).toLocaleString("es-CL", {
            day: "2-digit",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
      );
    } catch {
      return <span className="text-fg text-xs">{value}</span>;
    }
  }
  if (typeof value === "number") {
    return <span className="text-fg text-xs tabular-nums">{value.toLocaleString("es-CL")}</span>;
  }
  const s = String(value);
  if (s.length > 80) {
    return (
      <span className="text-fg text-xs" title={s}>
        {s.slice(0, 80)}…
      </span>
    );
  }
  return <span className="text-fg text-xs">{s}</span>;
}
